import { useEffect } from 'react';
import { Editor, Transforms, Descendant, Range } from 'slate';
import { ReactEditor } from 'slate-react';

interface UseEditorSyncOptions {
  editor: ReactEditor;
  initialValue?: Descendant[];
}

/**
 * Custom hook to synchronize Slate editor's internal state with external value changes.
 *
 * Why this approach?
 * Slate maintains two states we need to keep in sync:
 * 1. React state (editorValue) - For React's rendering and controlled components
 * 2. Editor state (editor.children) - Slate's internal representation
 *
 * We chose the Transform API over alternatives because:
 * - Direct mutation (editor.children = value) bypasses Slate's operations system
 * - Key-based remounting disrupts user experience and selection
 * - Transform API properly handles all edge cases and maintains history
 *
 * Implementation Details:
 * 1. Delete + Insert Pattern:
 *    We use delete + insert instead of direct replacement because:
 *    - It properly triggers all editor plugins and normalizations
 *    - It's recorded in the undo/redo history stack
 *    - It handles nested structures and preserves editor invariants
 *
 * 2. Selection Preservation:
 *    We store and restore selection because:
 *    - Maintains user's cursor position during updates
 *    - Critical for collaborative editing scenarios
 *    - Prevents focus loss during external updates
 *
 * The Transforms API is Slate's way of making changes to the editor state:
 * - Transforms.delete: Removes content in a specific range
 * - Transforms.insertNodes: Inserts new nodes at a specific location
 * - Transforms.select: Updates the editor's current selection
 *
 * Selection in Slate is represented by:
 * - anchor: Where the selection starts (like when you click your mouse)
 * - focus: Where the selection ends (like when you drag your mouse)
 * Both points are represented by a path (location in document tree) and offset
 *
 * Edge Cases Handled:
 * - Invalid selections after content changes
 * - Nested document structures
 * - Plugin interactions and normalizations
 * - Undo/redo history preservation
 *
 * @param options.editor - The Slate editor instance
 * @param options.initialValue - The new content to sync into the editor
 */
export function useEditorSync({ editor, initialValue }: UseEditorSyncOptions) {
  useEffect(() => {
    if (!initialValue) return;

    if (JSON.stringify(editor.children) === JSON.stringify(initialValue)) {
      return;
    }

    // Store current selection to restore it later
    // Selection is null when editor is not focused
    const previousSelection = editor.selection;

    try {
      // Delete everything in the editor
      // We use Editor.start/end to get the full document range
      Transforms.delete(editor, {
        at: {
          anchor: Editor.start(editor, []), // Start of document: path [0, 0], offset 0
          focus: Editor.end(editor, []) // End of document: last path, last offset
        }
      });

      // Insert the new content at the beginning
      Transforms.insertNodes(editor, initialValue);

      // Try to restore the previous selection if it existed
      if (previousSelection) {
        // Only restore if it's a valid range
        // This prevents errors if the selection points to now-deleted content
        if (
          Range.isRange(previousSelection) &&
          Editor.hasPath(editor, previousSelection.anchor.path)
        ) {
          Transforms.select(editor, previousSelection);
        }
      }
    } catch (e) {
      console.error('Error syncing editor content:', e);
    }
  }, [initialValue, editor]);
}
